import React, { useState } from 'react'
import { Icon, Tooltip, Button } from 'antd'
import { ImagePreview } from '../../../../components'
import LocationSelectionModal from './components/LocationSelectionModal'
import CharacterSelectionModal from './components/CharacterSelectionModal'
import PropSelectionModal from './components/PropSelectionModal'
import './EntitySelectorList.scss'
import WardrobeSelectorModal from '../ShotList/components/Wardrobe/WardrobeSelectorModal'

const EntitySelectorList = ({
  activePrompt,
  onUpdatePrompt, // function(newPrompt)
  formData,
  disabled = false
}) => {
  const [locationModalVisible, setLocationModalVisible] = useState(false)
  const [tagToReplace, setTagToReplace] = useState(null)
  const [charModalVisible, setCharModalVisible] = useState(false)
  const [charTagToReplace, setCharTagToReplace] = useState(null)
  const [wardrobeModalVisible, setWardrobeModalVisible] = useState(false)
  const [wardrobeCharKey, setWardrobeCharKey] = useState(null)
  const [wardrobeCharName, setWardrobeCharName] = useState(null)
  const [propModalVisible, setPropModalVisible] = useState(false)
  const [propTagToReplace, setPropTagToReplace] = useState(null)

  // 1. Location Parsing
  const getAllLocationsFromPrompt = text => {
    if (!text) return []
    const matches = text.matchAll(/__(location[\d\.a-zA-Z]+)__/g)
    return Array.from(matches, m => m[0])
  }

  // 2. Character Parsing
  const getAllCharactersFromPrompt = text => {
    if (!text) return []
    const allTags = Array.from(text.matchAll(/__([\s\S]+?)__/g), m => m[0])
    return allTags.filter(
      tag =>
        !tag.match(/^__location[\d\.a-zA-Z]+__$/) &&
        !tag.match(/^__outfit[\d\.a-zA-Z]+__$/) &&
        !tag.match(/^__prop[\d\.a-zA-Z]+__$/)
    )
  }

  // 3. Outfit Parsing
  const getAllOutfitsFromPrompt = text => {
    if (!text) return []
    const matches = text.matchAll(/__(outfit[\d\.a-zA-Z]+)__/g)
    return Array.from(matches, m => m[0])
  }

  // 4. Prop Parsing
  const getAllPropsFromPrompt = text => {
    if (!text) return []
    const matches = text.matchAll(/__(prop[\d\.a-zA-Z]+)__/g)
    return Array.from(matches, m => m[0])
  }

  const detectedLocations = getAllLocationsFromPrompt(activePrompt)
  const detectedCharacters = getAllCharactersFromPrompt(activePrompt)
  const detectedOutfits = getAllOutfitsFromPrompt(activePrompt)
  const detectedProps = getAllPropsFromPrompt(activePrompt)

  const handleLocationSelect = key => {
    const rawKey = key.replace(/^__/, '').replace(/__$/, '')
    const newTag = `__${rawKey}__`
    let newText = activePrompt || ''

    if (tagToReplace) {
      newText = newText.replace(tagToReplace, newTag)
    } else {
      newText = `${newTag}, ${newText}`
    }

    onUpdatePrompt && onUpdatePrompt(newText)
    setLocationModalVisible(false)
    setTagToReplace(null)
  }

  const openLocationModal = fullTag => {
    setTagToReplace(fullTag)
    setLocationModalVisible(true)
  }

  const handleCharacterSelect = name => {
    const newTag = `__${name}__`
    let newText = activePrompt || ''

    if (charTagToReplace) {
      newText = newText.replace(charTagToReplace, newTag)
    } else {
      newText = `${newTag}, ${newText}`
    }

    onUpdatePrompt && onUpdatePrompt(newText)
    setCharModalVisible(false)
    setCharTagToReplace(null)
  }

  const openCharModal = fullTag => {
    setCharTagToReplace(fullTag)
    setCharModalVisible(true)
  }

  const openWardrobeModal = fullTag => {
    // We need to find which character this outfit belongs to
    // formData.outfits_reference = { "__character1__": { "__outfit1__": "ID", ... } }
    const outfitsRef = formData.outfits_reference || {}
    let foundCharKey = null
    let foundCharName = null

    Object.entries(outfitsRef).forEach(([charKey, outfitMapByChar]) => {
      if (outfitMapByChar && outfitMapByChar[fullTag]) {
        foundCharKey = charKey
      }
    })

    if (foundCharKey) {
      const charName = foundCharKey.replace(/^__|__$/g, '')
      setWardrobeCharKey(foundCharKey)
      setWardrobeCharName(charName)
      setWardrobeModalVisible(true)
    } else {
      // Fallback: If not explicitly mapped, we might need a way to pick a character
      // or just show a message. For now, let's look for a character in the same prompt
      const firstChar = detectedCharacters[0]
      if (firstChar) {
        setWardrobeCharKey(firstChar)
        setWardrobeCharName(firstChar.replace(/^__|__$/g, ''))
        setWardrobeModalVisible(true)
      } else {
        // notification.info({ message: 'Please assign this outfit to a character first.' })
      }
    }
  }

  const handlePropSelect = key => {
    const rawKey = key.replace(/^__/, '').replace(/__$/, '')
    const newTag = `__${rawKey}__`
    let newText = activePrompt || ''

    if (propTagToReplace) {
      newText = newText.replace(propTagToReplace, newTag)
    } else {
      newText = `${newTag}, ${newText}`
    }

    onUpdatePrompt && onUpdatePrompt(newText)
    setPropModalVisible(false)
    setPropTagToReplace(null)
  }

  const openPropModal = fullTag => {
    setPropTagToReplace(fullTag)
    setPropModalVisible(true)
  }

  // Mapping to Entities
  const locationEntities = detectedLocations.map(fullTag => {
    const rawKey = fullTag.replace(/^__/, '').replace(/__$/, '')
    const refs = formData.locationReference || {}
    const refKey = refs[fullTag] ? fullTag : rawKey
    const reference = refs[refKey]
    const locationImage =
      reference &&
      (reference.uri || reference.url || reference.image || reference.src)
    const locationDesc = formData.locationMap && formData.locationMap[fullTag]
    const isValid = !!locationDesc

    return {
      key: `loc-${fullTag}`,
      tag: fullTag,
      image: locationImage,
      title: rawKey,
      subtitle: locationDesc,
      isValid,
      icon: 'environment',
      onAction: openLocationModal
    }
  })

  const characterEntities = detectedCharacters.map(fullTag => {
    const rawName = fullTag.replace(/^__/, '').replace(/__$/, '')
    const characterList = formData.characterList || []
    const selectedCharacters = formData.selectedCharacter || []

    const mapping = selectedCharacters.find(
      m => m.original === fullTag || m.current === fullTag
    )
    const resolvedName = mapping
      ? mapping.current.replace(/^__|__$/g, '')
      : rawName

    const character = characterList.find(c => c.name === resolvedName)
    const charImage = character?.attributes?.profile_pic
    const isValid = !!character

    return {
      key: `char-${fullTag}`,
      tag: fullTag,
      image: charImage,
      title: rawName,
      subtitle: character ? character.name : null,
      isValid,
      icon: 'user',
      onAction: openCharModal
    }
  })

  const outfitEntities = detectedOutfits.map(fullTag => {
    const rawKey = fullTag.replace(/^__/, '').replace(/__$/, '')
    const outfitDesc = formData.outfitMap && formData.outfitMap[fullTag]

    // Find image from library
    const outfitsRef = formData.outfits_reference || {}
    let outfitImage = null
    Object.values(outfitsRef).forEach(outfitMapByChar => {
      const outfitId = outfitMapByChar[fullTag]
      if (outfitId && formData.outfits) {
        const item = formData.outfits.find(o => (o.tag || o.id) === outfitId)
        if (item) {
          outfitImage =
            item.primary_image || (item.views && Object.values(item.views)[0])
        }
      }
    })

    const isValid = !!outfitDesc

    return {
      key: `outfit-${fullTag}`,
      tag: fullTag,
      image: outfitImage,
      title: rawKey,
      subtitle: outfitDesc,
      isValid,
      icon: 'skin',
      onAction: openWardrobeModal
    }
  })

  const propEntities = detectedProps.map(fullTag => {
    const rawKey = fullTag.replace(/^__/, '').replace(/__$/, '')
    const refs = formData.props_reference || formData.propReference || {}
    const refKey = refs[fullTag] ? fullTag : rawKey
    const reference = refs[refKey]
    const propImage =
      reference &&
      (reference.uri || reference.url || reference.image || reference.src)
    const propDesc = formData.propMap && formData.propMap[fullTag]
    const isValid = !!propDesc

    return {
      key: `prop-${fullTag}`,
      tag: fullTag,
      image: propImage,
      title: rawKey,
      subtitle: propDesc,
      isValid,
      icon: 'tool',
      onAction: openPropModal
    }
  })

  const entities = [
    ...locationEntities,
    ...characterEntities,
    ...outfitEntities,
    ...propEntities
  ]
  const hasEntities = entities && entities.length > 0

  return (
    <>
      <div className="entity-selectors-container">
        <div className={`selectors-tray ${!hasEntities ? 'empty' : ''}`}>
          <div className="tray-header">
            <span>
              Context Entities {hasEntities && `(${entities.length})`}
            </span>
          </div>
          <div className="tray-content">
            {hasEntities ? (
              entities.map((entity, index) => (
                <div key={`${entity.key}-${index}`} className="selector-item">
                  <div className="selector-content-wrapper">
                    {entity.isValid ? (
                      <>
                        {entity.image ? (
                          <div className="entity-avatar">
                            <ImagePreview
                              src={entity.image}
                              alt={entity.title}
                            />
                          </div>
                        ) : (
                          <div
                            className={`entity-avatar placeholder ${
                              entity.icon === 'environment'
                                ? 'environment'
                                : 'user'
                            }`}
                          >
                            <Icon type={entity.icon || 'question'} />
                          </div>
                        )}
                        <div className="entity-info">
                          <span className="entity-tag">{entity.tag}</span>
                          {entity.subtitle && (
                            <Tooltip title={entity.subtitle}>
                              <span className="entity-desc">
                                {entity.subtitle}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="entity-info error">
                        <span className="error-title">
                          <Icon type="warning" />
                          Unknown: {entity.tag}
                        </span>
                      </div>
                    )}
                  </div>

                  <Button
                    size="small"
                    type="link"
                    danger={!entity.isValid}
                    onClick={() => entity.onAction?.(entity.tag)}
                    disabled={disabled}
                    className="selector-action-btn"
                  >
                    {entity.isValid ? 'Change' : 'Fix'}
                  </Button>
                </div>
              ))
            ) : (
              <div className="tray-empty-state">
                <Icon type="info-circle" />
                <span>No location or character tags detected in prompt.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <LocationSelectionModal
        visible={locationModalVisible}
        onCancel={() => {
          setLocationModalVisible(false)
          setTagToReplace(null)
        }}
        onSelect={handleLocationSelect}
        locationMap={formData.locationMap}
        locationReference={formData.locationReference}
        currentLocationKey={tagToReplace}
      />
      <CharacterSelectionModal
        visible={charModalVisible}
        onCancel={() => {
          setCharModalVisible(false)
          setCharTagToReplace(null)
        }}
        onSelect={handleCharacterSelect}
        characterList={(formData.characterList || []).filter(c =>
          (formData.selectedCharacter || []).some(
            m => (m.current || '').replace(/^__|__$/g, '') === c.name
          )
        )}
        selectedCharacter={formData.selectedCharacter || []}
        currentCharacterKey={charTagToReplace}
      />
      <WardrobeSelectorModal
        visible={wardrobeModalVisible}
        onClose={() => {
          setWardrobeModalVisible(false)
          setWardrobeCharKey(null)
          setWardrobeCharName(null)
        }}
        characterName={wardrobeCharName}
        characterKey={wardrobeCharKey}
        wardrobe={formData.wardrobe}
        outfitMap={formData.outfitMap}
        wardrobeReference={formData.wardrobeReference}
        outfitsReference={formData.outfits_reference}
        onSave={(charKey, outfitTags) => {
          if (typeof formData.setFormData === 'function') {
            formData.setFormData(prev => ({
              ...prev,
              outfits_reference: {
                ...(prev.outfits_reference || {}),
                [charKey]: outfitTags
              }
            }))
          }
        }}
        formData={formData}
        setFormData={
          typeof formData.setFormData === 'function'
            ? formData.setFormData
            : () => {}
        }
      />
      <PropSelectionModal
        visible={propModalVisible}
        onCancel={() => {
          setPropModalVisible(false)
          setPropTagToReplace(null)
        }}
        onSelect={handlePropSelect}
        propMap={formData.propMap}
        propReference={formData.props_reference || formData.propReference}
        currentPropKey={propTagToReplace}
      />
    </>
  )
}

export default EntitySelectorList
